import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HTTPCONSTANTS } from '../http/http-constants';

/**
 * Release hosts (staging, www) talk to the production API, which has no
 * /account/saved or /account/followed endpoints. Saved plates and Track plate
 * fall back to the legacy lists the live site has always used:
 *
 *  - Save  -> userdetails/favourite/{reg}  (GET toggle; returns favourites)
 *  - Track -> userdetails/notify/{reg}     (GET toggle; returns notifies)
 *
 * Both endpoints TOGGLE, so callers only call them when their known state says
 * the plate is in the opposite state. The returned lists are incomplete on the
 * production API (favourites omit unlisted plates, notifies omit listed ones),
 * so the result is the toggle direction we asked for when the call succeeds,
 * not membership of the returned list.
 */
export class LegacyPlateLists {
  constructor(private http: HttpClient) {}

  private get base(): string {
    return `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/userdetails`;
  }

  private static regs(rows: any[] | null): string[] {
    return (rows || [])
      .map((r) => String(r?.registration || r?.formattedRegistration || ''))
      .map((r) => r.replace(/\s+/g, '').toUpperCase())
      .filter(Boolean);
  }

  public favourites(): Observable<string[]> {
    return this.http.get<any[]>(`${this.base}/favourites`).pipe(map(LegacyPlateLists.regs));
  }

  public notifies(): Observable<string[]> {
    return this.http.get<any[]>(`${this.base}/notifies`).pipe(map(LegacyPlateLists.regs));
  }

  /** Flips the favourite flag; true when the API accepted the call. */
  public toggleFavourite(reg: string, term?: string): Observable<boolean> {
    const t = term ? `/${encodeURIComponent(term)}` : '';
    return this.http.get(`${this.base}/favourite/${encodeURIComponent(reg)}${t}`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /** Flips the notify flag; true when the API accepted the call. */
  public toggleNotify(reg: string): Observable<boolean> {
    return this.http.get(`${this.base}/notify/${encodeURIComponent(reg)}`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
